import { Flex, Tag, TagCloseButton, TagLabel } from '@chakra-ui/react';
import { checkHasFiltersApplied } from 'hooks/usePaginatedTemplatesSearch/usePaginatedTemplatesSearch.helpers';
import { TemplateFilters } from 'hooks/usePaginatedTemplatesSearch/usePaginatedTemplatesSearch.types';
import cloneDeep from 'lodash.clonedeep';
import { useToast } from 'modules/common/components/Toast';
import { useCallback } from 'react';
import { useTranslation } from 'react-i18next';

import { TEMPLATE_FILTERS } from './TemplateFilter.config';
import { TemplateFilterConfig } from './TemplatesSearch.types';

interface TemplatesFilterTagsProps {
  appliedFilters: TemplateFilters | null;
  applyFilters: (filters: TemplateFilters) => void;
}

/**
 * Renders list of applied filters for templates
 */
export const TemplatesFilterTags = ({ appliedFilters, applyFilters }: TemplatesFilterTagsProps) => {
  const hasFiltersApplied = checkHasFiltersApplied(appliedFilters);

  // No filters applied
  if (!hasFiltersApplied) return null;

  return (
    <Flex gap={2} overflow='auto' w='full' data-testid='filter-tag-list'>
      {TEMPLATE_FILTERS.map((filterConfig) => (
        <FilterTag
          key={`filter-tag-${filterConfig.labelKey}`}
          applyFilters={applyFilters}
          filterConfig={filterConfig}
          appliedFilters={appliedFilters}
        />
      ))}
    </Flex>
  );
};

interface FilterTagProps {
  filterConfig: TemplateFilterConfig;
  appliedFilters: TemplateFilters | null;
  applyFilters: (filters: TemplateFilters) => void;
}

const FilterTag = ({
  appliedFilters,
  applyFilters,
  filterConfig: { labelKey, checkIsApplied, toggleFilter },
}: FilterTagProps) => {
  const { t } = useTranslation('editor_widget_menu', { useSuspense: false, keyPrefix: 'sideMenu.templatesMenu' });
  const translationKey = labelKey as unknown as TemplateStringsArray;
  const labelId = `${labelKey}-label`;
  const clearFilterLabel = t('filters.clearFilter');
  const clearSuccessTitle = t('filters.clearSuccess');

  const toast = useToast();

  const handleClose = useCallback(() => {
    const newFilters = cloneDeep(appliedFilters) || {};
    toggleFilter(newFilters);
    applyFilters(newFilters);
    toast({ title: clearSuccessTitle });
  }, [toggleFilter, applyFilters, appliedFilters, toast, clearSuccessTitle]);

  if (!appliedFilters) return null;
  if (appliedFilters && !checkIsApplied(appliedFilters)) return null;

  return (
    <Tag variant='filter-tag' size='md' minW='unset' data-testid={`filter-tag-${labelKey}`} h={'full'}>
      <TagLabel id={labelId} fontSize='xs'>
        {t(translationKey)}
      </TagLabel>
      <TagCloseButton
        data-testid='filter-tag-close-btn'
        onClick={handleClose}
        aria-label={clearFilterLabel}
        aria-describedby={labelId}
      />
    </Tag>
  );
};
